import { useEffect, useState } from "react";
import { api, label } from "../api.js";

/**
 * Примеры целей — второй корпус проекта, файлы в git.
 *
 * Карточка показывает не тему, а СПОСОБ: проверяемый критерий, правило, по которому
 * судит код, ритм протокола и то, что попадает в касание. Работа при заведении цели
 * в том и состоит, чтобы дожать формулировку до машинно-проверяемой; пример показывает,
 * как выглядит уже дожатая.
 *
 * ⚠️ Неправильных формулировок здесь нет — решение владельца 09.09. Пример учит образцом,
 * а не разбором чужих ошибок: зачёркнутая строка запоминается не хуже правильной,
 * а места занимает столько же.
 *
 * ⚠️ Один пример намеренно БЕЗ машинного правила. Не всякая цель сводится к числу,
 * и библиотека, где числовое правило есть у всех, научит подгонять цель под метрику —
 * а подгонка под метрику и есть подделка успеха.
 */

export type GoalExample = {
  slug: string;
  title: string;
  goal_type: string;
  horizon_bucket: string;
  domain: string;
  success_criterion: string;
  proof_rule: string;
  machine_rule?: { metric: string; op: string; value: number; unit?: string };
  protocol: { cadence: string; window_days: number; metric_name: string; metric_direction: "up" | "down" };
  touches: { day: string; kind: string; obstacle_code: string; note?: string }[];
  methods: string[];
  why_it_works: string;
};

/** Что переносится в форму: оси и форма правила. Название и критерий — нет, они твои. */
export type ExampleSeed = {
  goal_type: string;
  horizon_bucket: string;
  domain: string;
  metric: string;
  op: string;
  value: string;
};

export function ExamplesScreen({ onUse }: { onUse: (seed: ExampleSeed) => void }): React.JSX.Element {
  const [examples, setExamples] = useState<GoalExample[] | null>(null);
  const [type, setType] = useState<string>("all");

  useEffect(() => {
    void api.get<{ examples: GoalExample[] }>("/api/goal-examples").then((data) => setExamples(data.examples));
  }, []);

  if (!examples) return <p className="empty">читаю корпус…</p>;
  if (examples.length === 0) return <p className="empty">Примеров пока нет: корпус пополняется файлами в репозитории.</p>;

  const types = [...new Set(examples.map((example) => example.goal_type))];
  const shown = type === "all" ? examples : examples.filter((example) => example.goal_type === type);

  return (
    <>
      <p className="subhead">
        Не цели, а образцы заполнения: как выглядит проверяемый критерий, какой к нему
        протокол и что попадает в касание.
      </p>

      <div className="chips-row">
        <button className={`chip ${type === "all" ? "on" : ""}`} onClick={() => setType("all")}>
          все
        </button>
        {types.map((value) => (
          <button key={value} className={`chip ${type === value ? "on" : ""}`} onClick={() => setType(value)}>
            {label(value)}
          </button>
        ))}
      </div>

      {shown.map((example) => (
        <section className="sample-card" key={example.slug}>
          <div className="top">
            <span className="name">{example.title}</span>
            <span className="axes">
              {label(example.goal_type)} · {label(example.horizon_bucket)} · {label(example.domain)}
            </span>
          </div>

          <div className="pair">
            <span className="good">«{example.success_criterion}»</span>
          </div>

          <dl className="grid">
            <dt>доказательство</dt>
            <dd>{example.proof_rule}</dd>
            <dt>судит</dt>
            <dd>
              {example.machine_rule ? (
                <span className="num">
                  код: {example.machine_rule.metric} {example.machine_rule.op} {example.machine_rule.value}
                  {example.machine_rule.unit ? ` ${example.machine_rule.unit}` : ""}
                </span>
              ) : (
                "модель по качественному правилу — числа тут нет"
              )}
            </dd>
            <dt>протокол</dt>
            <dd>
              {example.protocol.cadence} · окно <span className="num">{example.protocol.window_days} дней</span>
            </dd>
            <dt>метрика</dt>
            <dd>
              <span className="num">{example.protocol.metric_name}</span>,{" "}
              {example.protocol.metric_direction === "up" ? "растёт" : "падает"}
            </dd>
            <dt>методика</dt>
            <dd>{example.methods.join(" · ")}</dd>
          </dl>

          <div className="touch-sample">
            <span className="k">что попадает в касание</span>
            {example.touches.map((touch, index) => (
              <div className="row-ex" key={`${touch.day}-${index}`}>
                <span className="d">{touch.day}</span>
                <span>
                  {label(touch.kind)}
                  {touch.note ? ` · ${touch.note}` : ""} · препятствие: {label(touch.obstacle_code)}
                </span>
              </div>
            ))}
          </div>

          <p className="why">{example.why_it_works}</p>

          <div className="acts">
            {/* Переносим оси и форму правила, но НЕ название с критерием: подставленный
                чужой критерий легко оставить как есть, и цель окажется не твоей. */}
            <button
              className="linklike"
              onClick={() =>
                onUse({
                  goal_type: example.goal_type,
                  horizon_bucket: example.horizon_bucket,
                  domain: example.domain,
                  metric: example.machine_rule?.metric ?? "",
                  op: example.machine_rule?.op ?? ">=",
                  value: example.machine_rule ? String(example.machine_rule.value) : ""
                })
              }
            >
              заполнить оси и метрику
            </button>
            <span className="from">пример из репозитория</span>
          </div>
        </section>
      ))}

      <p className="invariant">
        Примеры пополняются оффлайн и ревьюятся в git — как корпус методик. Кнопка переносит
        оси, метрику и вид правила; название и критерий пишешь сам, иначе цель будет не твоя.
      </p>
    </>
  );
}
